import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackPromoReveal, trackPromoCopy } from '../../../utils/promoStats';
import { openPartnerLink } from '../../../utils/openPartnerLink';
import { getPromoActionLabel } from '../../../utils/promoUi';

interface Promocode {
  id: string;
  store: string;
  title: string;
  code: string;
  discount: string;
  description: string;
  expiryDate: string;
  isExclusive: boolean;
  category: string;
  partnerUrl: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: string;
}

export default function NewPromocodes() {
  const navigate = useNavigate();
  const [revealedCodes, setRevealedCodes] = useState<Set<string>>(new Set());
  const [promocodes, setPromocodes] = useState<Promocode[]>([]);

  useEffect(() => {
    // Загружаем промокоды из localStorage
    const savedPromocodes = localStorage.getItem('promocodes');
    if (savedPromocodes) {
      const allPromocodes = JSON.parse(savedPromocodes);
      // Показываем только активные промокоды, отсортированные по дате создания
      const activePromocodes = allPromocodes
        .filter((promo: Promocode) => promo.isActive)
        .sort((a: Promocode, b: Promocode) => 
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
        .slice(0, 4); // Показываем только 4 последних
      setPromocodes(activePromocodes);
    }
  }, []);

  const handleGetCode = (promoId: string, code: string, partnerUrl: string) => {
    // Показываем промокод
    setRevealedCodes(prev => new Set([...prev, promoId]));
    
    // Копируем код в буфер обмена
    if (code) {
      navigator.clipboard.writeText(code);
    }

    // Трекинг
    const promo = promocodes.find(p => p.id === promoId);
    if (promo) {
      const trackData = {
        promoId,
        promoCode: code,
        promoTitle: promo.title,
        storeName: promo.store,
        storeId: promoId,
        discount: promo.discount,
      };
      trackPromoReveal(trackData);
      trackPromoCopy(trackData);
    }
    
    // Открываем партнерскую ссылку в новой вкладке
    openPartnerLink(partnerUrl);
  };

  const isCodeRevealed = (promoId: string) => revealedCodes.has(promoId);

  if (promocodes.length === 0) {
    return null;
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Новые промокоды</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Свежие промокоды и акции, добавленные сегодня
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {promocodes.map((promo) => (
            <div
              key={promo.id}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-sm font-medium text-blue-600">{promo.store}</span>
                    {promo.isExclusive && (
                      <span className="bg-orange-100 text-orange-600 px-2 py-1 rounded-full text-xs font-semibold">
                        Эксклюзив
                      </span>
                    )}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{promo.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{promo.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span>До {promo.expiryDate}</span>
                    <span>•</span>
                    <span>{promo.category}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-sm font-semibold mb-3">
                    {promo.discount}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className={`font-mono font-bold text-gray-900 ${!isCodeRevealed(promo.id) ? 'filter blur-sm select-none' : ''}`}>
                      {promo.code}
                    </span>
                    {isCodeRevealed(promo.id) && (
                      <button
                        onClick={() => navigator.clipboard.writeText(promo.code)}
                        className="text-blue-600 hover:text-blue-700 cursor-pointer"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleGetCode(promo.id, promo.code, promo.partnerUrl)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isCodeRevealed(promo.id) ? 'Получено!' : getPromoActionLabel(promo.code)}
                </button>
              </div>
            </div>
          ))}
        </div>

        {promocodes.length >= 4 && (
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/stores')}
              className="bg-blue-600 text-white px-8 py-3 rounded-full hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Все новые промокоды
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
